import { Link } from "react-router-dom";
import {
  HeartHandshake, Home as HomeIcon, Utensils, Pill, PersonStanding, Car, Moon, Brain, Activity,
  Shield, Users, Clock, Award, MapPin, ArrowRight, Quote, Star, Phone, CalendarDays, UserPlus, CheckCircle2,
} from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const SERVICES = [
  { icon: HeartHandshake, title: "Personal Care", desc: "Bathing, dressing, grooming and hygiene support delivered with dignity." },
  { icon: Users, title: "Companion Care", desc: "Friendly conversation, activities and emotional companionship." },
  { icon: Utensils, title: "Meal Preparation", desc: "Nutritious home-cooked meals tailored to dietary needs." },
  { icon: Pill, title: "Medication Reminders", desc: "Timely reminders so prescriptions are never missed." },
  { icon: PersonStanding, title: "Mobility Support", desc: "Safe transfers, walking assistance and fall prevention." },
  { icon: Car, title: "Transportation", desc: "Rides to appointments, errands and social outings." },
  { icon: Moon, title: "Respite Care", desc: "Short-term relief so family caregivers can rest and recharge." },
  { icon: Brain, title: "Alzheimer's & Dementia Care", desc: "Specialised memory care from trained caregivers." },
  { icon: Activity, title: "Post-Hospital Recovery", desc: "Gentle support during the critical transition home." },
];

const TRUST = [
  { icon: Shield, t: "Background-checked caregivers", d: "Every caregiver passes rigorous screening." },
  { icon: Award, t: "Trained & certified team", d: "Ongoing training in safety, dignity and dementia care." },
  { icon: Clock, t: "Flexible scheduling", d: "Hourly, overnight, live-in or 24/7 — built around you." },
  { icon: HeartHandshake, t: "Family-centered updates", d: "Clear communication keeps families in the loop." },
];

const TESTIMONIALS = [
  { name: "Denise R.", role: "Daughter of client", quote: "Melo sent the most patient, warm caregiver we've ever met. Mom lights up when she arrives — we finally breathe easier knowing she's in good hands.", rating: 5 },
  { name: "Marcus W.", role: "Son of client", quote: "After dad's hospital stay, their post-recovery team made the transition home seamless. Professional, compassionate, and always on time.", rating: 5 },
  { name: "Sarah L.", role: "Care recipient", quote: "I feel safe and truly seen. It's not just help — it's hope showing up at my door every morning.", rating: 5 },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white">
        <div className="grain" />
        <div className="container-x pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 pill bg-teal-50 text-teal-700 border border-teal-100" data-testid="hero-eyebrow">
              <span className="heart-dot" /> The Heart of Home Care · MO · NE · IA
            </div>
            <h1 className="mt-6 text-[40px] sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight text-slate-900 leading-[1.05]" data-testid="hero-headline">
              Compassionate Care.<br />
              Reliable Support.{" "}
              <span className="brand-gradient-text">Hope at Home.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed" data-testid="hero-subheadline">
              Melo Hopeful Care provides non-medical in-home care for seniors and adults who need a steady hand,
              a warm presence, and help with daily living — all delivered with the dignity every family deserves.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-group">
              <Link to="/request-care" className="btn-primary" data-testid="hero-request-care-btn">
                <HeartHandshake className="w-4 h-4" /> Request Care
              </Link>
              <Link to="/consultation" className="btn-outline" data-testid="hero-consultation-btn">
                <CalendarDays className="w-4 h-4" /> Schedule Consultation
              </Link>
              <Link to="/careers" className="btn-ghost" data-testid="hero-careers-btn">
                <UserPlus className="w-4 h-4" /> Apply as Caregiver <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-slate-500" data-testid="hero-trust-row">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600" /> Background-checked caregivers</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600" /> 24/7 availability</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600" /> Family-first communication</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up">
            <div className="relative rounded-[28px] overflow-hidden shadow-[0_30px_60px_-20px_rgba(11,59,96,0.35)]">
              <img
                src="https://images.unsplash.com/photo-1764006145420-df3006edf060?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Caregiver helping senior"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-white/95 backdrop-blur p-5 shadow-lg">
                <div className="flex items-center gap-3">
                  <img src={LOGO_URL} className="w-12 h-12 object-contain" alt="Melo" />
                  <div>
                    <div className="text-sm text-slate-500">Care coordinator on call</div>
                    <a href="tel:+14027611531" className="text-lg font-semibold text-slate-900">(402) 761-1531</a>
                  </div>
                  <a href="tel:+14027611531" className="ml-auto btn-primary !py-2 !px-4" data-testid="hero-floating-call-btn"><Phone className="w-4 h-4" /> Call now</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST SIGNALS */}
      <section className="section bg-slate-50">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="eyebrow">Why families choose us</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Trust that shows up, every shift.</h2>
            </div>
            <p className="md:max-w-md text-slate-600">We are built on four promises that families tell us matter most when inviting someone into their home.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TRUST.map(({ icon: Icon, t, d }) => (
              <div key={t} className="soft-card" data-testid={`trust-card-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500/15 to-brand-navy/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-brand-navy" strokeWidth={1.75} />
                </div>
                <div className="mt-5 font-heading font-semibold text-slate-900">{t}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="section" data-testid="home-services-section">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="eyebrow">What we do</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Nine ways we make daily life easier at home.</h2>
            <p className="mt-4 text-slate-600">From a few hours a week to around-the-clock support, every care plan is shaped around the person — never the clock.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="soft-card group" data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-slate-900">{title}</div>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/services" className="btn-outline" data-testid="home-view-all-services">
              View all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CALL-NOW BANNER */}
      <section className="relative overflow-hidden brand-gradient text-white" data-testid="home-call-now-banner">
        <div className="container-x pt-16 md:pt-20 pb-6 md:pb-8 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-100 font-semibold">Urgent care need?</div>
            <h3 className="mt-3 text-3xl md:text-4xl font-heading font-semibold text-white">We can be at the door in hours, not days.</h3>
            <p className="mt-3 text-white/90 max-w-xl">Call a real care coordinator — nights, weekends and holidays included.</p>
          </div>
          <a href="tel:+14027611531" className="inline-flex items-center gap-3 bg-white text-brand-navy rounded-full px-7 py-4 text-base font-semibold hover:-translate-y-0.5 transition shadow-lg" data-testid="home-call-now-btn">
            <Phone className="w-5 h-5" /> (402) 761-1531
          </a>
        </div>
        <div className="container-x pb-12 md:pb-14">
          <p className="text-xs text-white/85">
            Not for medical emergencies — call <strong className="text-white">911</strong> immediately in a life-threatening situation.
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="eyebrow">Real stories</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Loved ones, loved back.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="soft-card flex flex-col" data-testid={`testimonial-${i}`}>
                <Quote className="w-7 h-7 text-teal-500" />
                <p className="mt-4 text-slate-700 leading-relaxed">{t.quote}</p>
                <div className="mt-6 flex items-center gap-0.5">
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="section bg-slate-50">
        <div className="container-x grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="eyebrow">Where we serve</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Proudly serving Missouri, Nebraska & Iowa.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Our caregivers are active across the Kansas City metro, the Omaha metro and Council Bluffs — with new
              service areas opening as our family of caregivers continues to grow.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-2.5">
              {["Kansas City, MO", "Lee's Summit", "Liberty", "Independence", "Omaha, NE", "Bellevue, NE", "Papillion, NE", "Council Bluffs, IA"].map((a) => (
                <li key={a} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`service-area-chip-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <MapPin className="w-4 h-4 text-teal-600" /> {a}
                </li>
              ))}
            </ul>
            <Link to="/service-areas" className="btn-outline mt-8" data-testid="home-view-service-areas">
              See full service area <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative rounded-[28px] overflow-hidden shadow-xl">
            <img src="https://images.unsplash.com/photo-1758686253692-2d6921de12dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Happy elderly couple at home" className="w-full h-[480px] object-cover" />
          </div>
        </div>
      </section>

      {/* REFERRAL TEASER */}
      <section className="section">
        <div className="container-x">
          <div className="soft-card md:p-14 bg-gradient-to-br from-white to-teal-50/30 border-teal-100/60">
            <div className="flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <div className="eyebrow">For hospitals, rehab & social workers</div>
                <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">Partner with Melo Hopeful Care.</h2>
                <p className="mt-4 text-slate-600 max-w-xl leading-relaxed">
                  Give your patients a soft landing. We coordinate rapid intake for hospital discharges,
                  rehab transitions and case-managed clients across the region.
                </p>
              </div>
              <Link to="/referral-partners" className="btn-primary" data-testid="home-become-partner-btn">
                Become a partner <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
